import { Accessor, Show } from 'solid-js';
import { FieldType, FormFieldContext } from '../forms-types';
import { FieldErrorMessage, FormInput, FormTextarea } from './primitives';
import { getUniqueSlug } from '../utils';

type FieldProps = {
    field: FieldType;
    ctx: FormFieldContext;
};

type TextInputProps = FieldProps & {
    forceRequire?: Accessor<boolean>;
};

const slugTranslationOverride: Record<string, string> = {
    firstname: 'first-name',
    lastname: 'last-name',
    zip: 'postal-code',
};

const inputAutocompleteBySlug: Record<string, { autocomplete: string; type?: string }> = {
    email: { autocomplete: 'email', type: 'email' },
    phone: { autocomplete: 'tel', type: 'tel' },
    company: { autocomplete: 'organization' },
};

export const TextInput = (props: TextInputProps) => {
    const { formValues, errors, onInputChange, localize } = props.ctx;

    const slug = () => props.field.slug;
    const required = () => !!(props.field.required || props.forceRequire?.());
    const translationKey = () => slugTranslationOverride[slug()] ?? slug();
    const autocompleteEntry = () => inputAutocompleteBySlug[slug()];
    const placeholder = () => `${localize(translationKey(), props.field.name)}${required() ? '*' : ''}`;

    return (
        <div>
            <FormInput
                required={required()}
                value={formValues()[slug()]}
                onChange={(e) => onInputChange(slug(), e.currentTarget.value)}
                placeholder={placeholder()}
                hasErrors={!!errors()[slug()]}
                name={slug()}
                aria-label={localize(translationKey(), props.field.name)}
                aria-describedby={`desc-${slug()}`}
                title={placeholder()}
                {...autocompleteEntry()?.autocomplete ? { autocomplete: autocompleteEntry()!.autocomplete } : {}}
                type={autocompleteEntry()?.type}
            />

            <span id={`desc-${slug()}`} style={{ display: 'none' }}>
                {placeholder()}
            </span>

            <Show when={errors()[slug()]}>
                <FieldErrorMessage errors={errors} fieldKey={slug()} />
            </Show>
        </div>
    );
};

// Custom WP text field: slug/name/eloqua-value comes from the editor-supplied
// extraSettings.name.value, not the field slug.
export const TextField = (props: FieldProps) => {
    const { formValues, errors, onInputChange, localize } = props.ctx;

    const extraSettings = () => props.field.extraSettings;
    const placeholder = () => `${extraSettings()?.placeholder?.value || localize('text-field', 'Text field')}` + (props.field.required ? '*' : '');
    const eloquaName = () => extraSettings()?.name.value ?? '';
    const uniqueSlug = () => getUniqueSlug(props.field);

    return (
        <Show when={extraSettings()}>
            <div>
                <FormInput
                    name={'custom-text-field-' + eloquaName()}
                    required={props.field.required}
                    value={formValues()[uniqueSlug()]}
                    onChange={(e) => onInputChange(eloquaName(), e.currentTarget.value, props.field.slug)}
                    placeholder={placeholder()}
                    hasErrors={!!errors()[uniqueSlug()]}
                    aria-label={placeholder()}
                    aria-describedby={`desc-${uniqueSlug()}`}
                    title={placeholder()}
                />

                <span id={`desc-${uniqueSlug()}`} style={{ display: 'none' }}>
                    {placeholder()}
                </span>

                <Show when={errors()[uniqueSlug()]}>
                    <FieldErrorMessage errors={errors} fieldKey={uniqueSlug()} />
                </Show>
            </div>
        </Show>
    );
};

export const TextArea = (props: FieldProps) => {
    const { formValues, errors, onInputChange, localize } = props.ctx;

    const placeholder = () => `${props.field.extraSettings?.placeholder?.value || localize('text-area', 'Text area')}` + (props.field.required ? '*' : '');
    const uniqueSlug = () => getUniqueSlug(props.field);

    return (
        <div>
            <FormTextarea
                required={props.field.required}
                value={formValues()[uniqueSlug()]}
                onChange={(e) => onInputChange(uniqueSlug(), e.currentTarget.value, props.field.slug)}
                placeholder={placeholder()}
                name={'custom-text-area-' + props.field.slug}
                rows={6}
                aria-label={placeholder()}
                aria-describedby={`desc-${uniqueSlug()}`}
                title={placeholder()}
                hasErrors={!!errors()[uniqueSlug()]}
            />

            <span id={`desc-${uniqueSlug()}`} style={{ display: 'none' }}>
                {placeholder()}
            </span>

            <Show when={errors()[uniqueSlug()]}>
                <FieldErrorMessage errors={errors} fieldKey={uniqueSlug()} />
            </Show>
        </div>
    );
};

export const Comment = (props: FieldProps) => {
    const { formValues, errors, onInputChange, localize } = props.ctx;

    const uniqueSlug = () => getUniqueSlug(props.field);

    return (
        <div>
            <FormTextarea
                required={props.field.required}
                value={formValues()[uniqueSlug()]}
                onChange={(e) => onInputChange(uniqueSlug(), e.currentTarget.value)}
                placeholder={`${localize('comments', 'Comments')}${props.field.required ? '*' : ''}`}
                name={uniqueSlug()}
                rows={1}
                aria-label={localize('comments', 'Comments')}
                aria-describedby={`desc-${uniqueSlug()}`}
                title={localize('comments', 'Comments')}
                hasErrors={!!errors()[uniqueSlug()]}
            />

            <span id={`desc-${uniqueSlug()}`} style={{ display: 'none' }}>
                {localize('comments', 'Comments')}
            </span>

            <Show when={errors()[uniqueSlug()]}>
                <FieldErrorMessage errors={errors} fieldKey={uniqueSlug()} />
            </Show>
        </div>
    );
};
